export const parseWikipediaData = async (canonicalTitle: string, findImage: boolean = true) => {
    let url = `https://en.wikipedia.org/w/api.php?action=parse&format=json&formatversion=2&origin=*&page=${canonicalTitle}&prop=categories|externallinks|properties`;
    if (findImage === true) {
        url += `|text`;
    }
    
    try {
        const response = await fetch(url);
        const data = await response.json();

        if (!findImage) {
            return {
                wiki_parse_categories: data.parse.categories,
                wiki_parse_externallinks: data.parse.externallinks,
                wiki_parse_wikibase_shortdesc: data.parse.properties['wikibase-shortdesc']
            };
        }

        // Define the type for our regex matches
        type WikiImageMatch = [
            string,      // Full match
            string,      // Image URL
            string      // Width
        ];

        // Find all image matches with width attribute - now matches both attribute orders
        const allMatches = Array.from<RegExpMatchArray>(
            data.parse.text.matchAll(/(?:width="(\d+)"[^>]*src="(\/\/upload\.wikimedia\.org[^"]+)"|src="(\/\/upload\.wikimedia\.org[^"]+)"[^>]* width="(\d+)")/g)
        );

        // Only take the first two matches
        const firstTwoMatches = allMatches.slice(0, 2).map((match) => {
            // If width comes first (original pattern)
            if (match[1] && match[2]) {
                return [match[0], match[2], match[1]] as WikiImageMatch;
            }
            // If src comes first (new pattern)
            return [match[0], match[3], match[4]] as WikiImageMatch;
        });

        // Find the first image with width >= 50 from the first two matches
        const validImg = firstTwoMatches.find(match => {
            if (!match[2]) return false;
            const width = parseInt(match[2], 10);
            return !isNaN(width) && width >= 56;
        });

        const firstImgUrl = validImg 
            ? `https:${validImg[1]}` 
            : null;

        // Extract categories
        const categories = data.parse.categories;

        // Extract external links
        const externalLinks = data.parse.externallinks;

        // Extract wikibase-shortdesc
        const shortDesc = data.parse.properties['wikibase-shortdesc'];

        return {
            wiki_parse_first_img: firstImgUrl,
            wiki_parse_categories: categories,
            wiki_parse_externallinks: externalLinks,
            wiki_parse_wikibase_shortdesc: shortDesc
        };
    } catch (error) {
        console.error('Error parsing Wikipedia data:', error);
        throw error;
    }
};

/**
 * Converts a Wikipedia thumbnail URL to its full-resolution version
 * @param thumbnailUrl - The Wikipedia thumbnail URL
 * @returns The full-resolution image URL or null if invalid URL
 * 
 * Example:
 * Input: https://upload.wikimedia.org/wikipedia/commons/thumb/f/f9/Guy-Fawkes.jpg/320px-Guy-Fawkes.jpg
 * Output: https://upload.wikimedia.org/wikipedia/commons/f/f9/Guy-Fawkes.jpg
 */
export const getWikiFullResolutionUrl = (thumbnailUrl: string): string | null => {
    try {
        // Check if it's a valid Wikipedia Commons URL
        if (!thumbnailUrl.includes('upload.wikimedia.org/wikipedia/commons')) {
            return null;
        }

        // If it's not a thumbnail, return as is
        if (!thumbnailUrl.includes('/thumb/')) {
            return thumbnailUrl;
        }

        // Split the URL by '/'
        const urlParts = thumbnailUrl.split('/');
        
        // Remove 'thumb' from the path
        const thumbIndex = urlParts.indexOf('thumb');
        if (thumbIndex !== -1) {
            urlParts.splice(thumbIndex, 1);
        }

        // Remove the size specification (last part)
        urlParts.pop();

        // Rejoin the URL
        return urlParts.join('/');
    } catch (error) {
        console.error('Error processing Wikipedia image URL:', error);
        return null;
    }
};

